import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Body, Box, Icon } from '@hover/blueprint';
import { iChevronUp } from '@hover/icons';
import type { ReactNode } from 'react';
import React from 'react';

import { useRenderingContext } from 'src/modules/renderings/hooks';
import { Page, buttonPressed } from 'src/shared/telemetry';

interface AccordionItemProps {
  children: ReactNode;
  isActive: boolean;
  onToggleExpanded: () => void;
  title: string;
  lastItem: boolean;
  versionId?: string;
}

const AnimationBase = css`
  transition: all 0.5s ease;
`;

const RotateIcon = styled.div`
  color: #030303;
  ${AnimationBase}
  ${({ isActive }: { isActive: boolean }) =>
    isActive
      ? ''
      : css`
          -moz-transform: rotate(180deg);
          -webkit-transform: rotate(180deg);
          transform: rotate(180def);
        `}
`;

const AccordionContent = styled.div`
  ${AnimationBase}
  overflow: hidden;
  ${({ isActive }: { isActive: boolean }) =>
    isActive
      ? 'max-height: 1000px'
      : css`
          opacity: 0;
          max-height: 0;
        `}
`;

const createTrackId = (isActive: boolean, versionId?: string) => {
  const action = isActive ? 'hide' : 'show';
  if (versionId) {
    return `${action}-design-set-${versionId}`;
  }

  return `${action}-original-set`;
};

const AccordionItem = ({
  children,
  isActive,
  lastItem,
  onToggleExpanded,
  title,
  versionId,
}: AccordionItemProps) => {
  const { trackingData } = useRenderingContext();

  const handleTracking = () => {
    const buttonText = title;
    const trackId = createTrackId(isActive, versionId);

    const buttonPressedTrackingData = {
      ...trackingData,
      button_text: buttonText,
      page_or_screen_name: Page.RENDERINGS_HUB,
      primary_cta: false,
      verison_id: versionId,
      track_id: trackId,
    };

    buttonPressed(buttonPressedTrackingData);
  };

  const handleToggleActiveSet = () => {
    onToggleExpanded();
    handleTracking();
  };

  return (
    <Box
      borderBottom={lastItem ? undefined : '1px solid'}
      borderColor={lastItem ? undefined : 'neutral400'}
      flexDirection="column"
      paddingX={500}
      paddingY={400}
    >
      <Box
        alignItems="center"
        cursor="pointer"
        flexDirection="row"
        justifyContent="space-between"
        onClick={handleToggleActiveSet}
      >
        <Body
          color="neutral.600"
          fontWeight={700}
          isBold
          marginY={0}
          size={300}
          textTransform="uppercase"
        >
          {title}
        </Body>
        <RotateIcon isActive={isActive}>
          <Icon color="neutral500" icon={iChevronUp} size="medium" />
        </RotateIcon>
      </Box>
      <AccordionContent isActive={isActive}>{children}</AccordionContent>
    </Box>
  );
};

export { AccordionItem };
